import dayjs from 'dayjs';

import type { InstagramPostDto } from '@app/api/SocialIntegrations';
import {
  INSTAGRAM_PUBLICATIONS_LIMIT_ERROR,
  INSTAGRAM_PUBLICATIONS_REQUIRED_ERROR,
  MAX_SELECTED_INSTAGRAM_PUBLICATIONS,
  POSTS_REFRESH_INTERVAL_MS,
} from '@app/components/Modals/BotDetailsModal/constants/instagramPublications';
import {
  PublicationSelectionMode,
  type InstagramPublication,
  type PublicationSelectionTypeParameters,
} from '@app/components/Modals/BotDetailsModal/types';
import { DEFAULT_SOCIAL_INTEGRATION_ID_INDEX } from '@app/constants/defaultChannelIndex';
import { INSTAGRAM } from '@app/constants/socialIntegrationsTypes';

const PUBLICATION_DATE_FORMAT = 'DD.MM.YYYY';
const REFRESH_TIME_FORMAT = 'HH:mm';
const REFRESH_DATE_TIME_FORMAT = 'DD.MM.YYYY HH:mm';

// Метка типа. Рилс — только по Type. У single тип медиа различает фото и видео.
const getInstagramPostTypeLabel = ({
  Type,
  MediaType,
}: Pick<InstagramPostDto, 'Type' | 'MediaType'>): string => {
  if (Type === 'reels') return 'Рилс';
  if (Type === 'carousel') return 'Карусель';
  if (MediaType === 'IMAGE') return 'Фото';
  if (MediaType === 'VIDEO') return 'Видео';

  return 'Публикация';
};

// Выбор публикаций есть только у конкретной интеграции Instagram:
// у «Любой канал» и «Последняя интеграция» публикаций нет.
// Возвращает id интеграции для запроса публикаций или null.
export const getInstagramPublicationsIntegrationId = (
  socialIntegrationType: unknown,
  socialIntegrationId: unknown
): number | null => {
  if (
    socialIntegrationType !== INSTAGRAM ||
    socialIntegrationId === DEFAULT_SOCIAL_INTEGRATION_ID_INDEX ||
    (typeof socialIntegrationId !== 'number' &&
      typeof socialIntegrationId !== 'string')
  ) {
    return null;
  }

  const integrationId = Number(socialIntegrationId);

  return Number.isInteger(integrationId) && integrationId > 0
    ? integrationId
    : null;
};

// TypeParameters робота → поля формы. У старого робота полей нет — режим «все».
export const getPublicationSelectionFormValues = (
  typeParameters: Record<string, unknown> | undefined
): {
  publicationSelectionMode: PublicationSelectionMode;
  instagramPublicationIds: string[];
} => {
  const rawMode = typeParameters?.PublicationSelectionMode;
  const rawIds = typeParameters?.InstagramPublicationIds;

  return {
    publicationSelectionMode:
      rawMode === PublicationSelectionMode.Selected
        ? PublicationSelectionMode.Selected
        : PublicationSelectionMode.All,
    instagramPublicationIds: Array.isArray(rawIds)
      ? rawIds.filter((id): id is string => typeof id === 'string')
      : [],
  };
};

// Поля формы → TypeParameters. Без интеграции Instagram и в режиме «все»
// список не передаётся: бэкенд хранит его только для режима «выбранные».
export const getPublicationSelectionTypeParameters = ({
  publicationSelectionMode,
  instagramPublicationIds,
  isAvailable,
}: {
  publicationSelectionMode: PublicationSelectionMode | undefined;
  instagramPublicationIds: string[] | undefined;
  isAvailable: boolean;
}): PublicationSelectionTypeParameters =>
  isAvailable && publicationSelectionMode === PublicationSelectionMode.Selected
    ? {
        PublicationSelectionMode: PublicationSelectionMode.Selected,
        // Значение поля читается и на каждой перерисовке: берём только строки
        InstagramPublicationIds: Array.isArray(instagramPublicationIds)
          ? instagramPublicationIds.filter(
              (id): id is string => typeof id === 'string'
            )
          : [],
      }
    : {
        PublicationSelectionMode: PublicationSelectionMode.All,
        InstagramPublicationIds: null,
      };

// Текст ошибки поля выбора или null, если выбор можно сохранить.
export const getInstagramPublicationsError = (
  publicationSelectionMode: PublicationSelectionMode | undefined,
  instagramPublicationIds: string[] | undefined
): string | null => {
  if (publicationSelectionMode !== PublicationSelectionMode.Selected) {
    return null;
  }

  const selectedCount = instagramPublicationIds?.length ?? 0;

  if (!selectedCount) {
    return INSTAGRAM_PUBLICATIONS_REQUIRED_ERROR;
  }

  return selectedCount > MAX_SELECTED_INSTAGRAM_PUBLICATIONS
    ? INSTAGRAM_PUBLICATIONS_LIMIT_ERROR
    : null;
};

// Отметить или снять публикацию. Сверх предела новая отметка не ставится:
// функция возвращает тот же список.
export const toggleInstagramPublication = (
  selectedIds: string[],
  instagramId: string
): string[] => {
  if (selectedIds.includes(instagramId)) {
    return selectedIds.filter((id) => id !== instagramId);
  }

  return selectedIds.length >= MAX_SELECTED_INSTAGRAM_PUBLICATIONS
    ? selectedIds
    : [...selectedIds, instagramId];
};

// Склеить загруженные страницы. Шлюз дочитывает список в фоне, новая
// публикация сдвигает смещение, и следующая страница повторяет хвост
// предыдущей — повтор отбрасываем по instagramId.
export const mergeInstagramPublications = (
  loaded: InstagramPublication[],
  incoming: InstagramPublication[]
): InstagramPublication[] => {
  const loadedIds = new Set(loaded.map(({ instagramId }) => instagramId));

  return [
    ...loaded,
    ...incoming.filter(({ instagramId }) => !loadedIds.has(instagramId)),
  ];
};

// Сколько выбранных публикаций нет в загруженных страницах.
export const countUnloadedSelectedPublications = (
  selectedIds: string[],
  loaded: InstagramPublication[]
): number => {
  const loadedIds = new Set(loaded.map(({ instagramId }) => instagramId));

  return selectedIds.filter((id) => !loadedIds.has(id)).length;
};

export const mapInstagramPost = ({
  InstagramId,
  Type,
  MediaType,
  PreviewUrl,
  Caption,
  Permalink,
  PublishedAt,
}: InstagramPostDto): InstagramPublication => ({
  instagramId: InstagramId,
  isReels: Type === 'reels',
  typeLabel: getInstagramPostTypeLabel({ Type, MediaType }),
  previewUrl: PreviewUrl || null,
  caption: Caption?.trim() || null,
  permalink: Permalink?.trim() || null,
  publishedAtLabel: PublishedAt
    ? dayjs(PublishedAt).format(PUBLICATION_DATE_FORMAT)
    : null,
});

// Выбранные публикации без тех, которых нет в загруженном списке.
export const removeUnloadedSelectedPublications = (
  selectedIds: string[],
  loaded: InstagramPublication[]
): string[] => {
  const loadedIds = new Set(loaded.map(({ instagramId }) => instagramId));

  return selectedIds.filter((id) => loadedIds.has(id));
};

// Строка о фоновом обновлении списка. Шлюз начинает обход на каждый запрос,
// если прошлый начался 10 минут назад или раньше.
export const getPostsRefreshNote = (
  postsRefreshedAt: string | null,
  receivedAt: number
): string => {
  if (!postsRefreshedAt) {
    return 'Публикации загружаются из Instagram. Это займёт несколько минут. Потом нажмите «Обновить список».';
  }

  const refreshedAt = dayjs(postsRefreshedAt);

  if (receivedAt - refreshedAt.valueOf() < POSTS_REFRESH_INTERVAL_MS) {
    return `Обновление началось в ${refreshedAt.format(
      REFRESH_TIME_FORMAT
    )}. Новые публикации появятся в течение нескольких минут. Нажмите «Обновить список», чтобы их увидеть.`;
  }

  return `Обновление началось. Через несколько минут нажмите «Обновить список», чтобы увидеть новые публикации. Прошлое обновление — ${refreshedAt.format(
    REFRESH_DATE_TIME_FORMAT
  )}.`;
};
